import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Spinner,
  Alert,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';
import { Check, AlertCircle, Eye, EyeOff } from 'react-feather';

import { plans } from '../../Config/pitti-plans';

const validationSchema = yup.object().shape({
  type: yup.string().required('Please select a type'),
  entity: yup
    .object()
    .nullable()
    .required('Please select a brand or retailer'),
  personalName: yup.string().trim().required('Personal name is required'),
  businessEmail: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Business email is required'),
  role: yup.string().trim().optional().nullable(),
  phone: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/i.test(value);
      },
      message: 'Enter a valid phone number'
    }),
  password: yup
    .string()
    .trim()
    .min(7, 'Password must be at least 7 characters')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .required('Password is required'),
  enableCRM: yup.boolean().optional(),
  selectedPlan: yup.number().required('Please select a plan')
});

const ManualInviteTab = ({
  setupPittiBrandRetailerAccount,
  getAllBrands,
  getAllRetailers,
  checkOrganizationExist,
  onSuccess,
}) => {
  const [selectedType, setSelectedType] = useState('brand');
  const [organizationError, setOrganizationError] = useState(null);
  const [checkingOrganization, setCheckingOrganization] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    reset
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: 'brand',
      entity: null,
      personalName: '',
      businessEmail: '',
      role: '',
      phone: '',
      password: '',
      enableCRM: false,
      selectedPlan: null
    }
  });

  const watchedType = watch('type');
  const watchedPlan = watch('selectedPlan');

  useEffect(() => {
    if (watchedType !== selectedType) {
      setSelectedType(watchedType);
      setValue('entity', null);
      setOrganizationError(null);
    }
  }, [watchedType, selectedType, setValue]);

  const handleEntityChange = async (selectedOption, field) => {
    field.onChange(selectedOption);
    setOrganizationError(null);

    if (selectedOption?.value) {
      setCheckingOrganization(true);
      try {
        const result = await checkOrganizationExist(selectedOption.value, selectedType);
        if (result?.data?.exists) {
          setOrganizationError({
            message: `This ${selectedType} already has an organization associated with it.`,
            organization: result?.data?.organization
          });
        }
      } catch (error) {
        console.error('Error checking organization:', error);
      } finally {
        setCheckingOrganization(false);
      }
    }
  };

  const fetchEntities = useCallback(
    async (inputValue) => {
      try {
        let results = [];
        if (selectedType === 'brand') {
          results = await getAllBrands(1, {}, inputValue, 20, 'Pitti');
        } else {
          results = await getAllRetailers(1, {}, inputValue, 20, 'Pitti');
        }

        return (results || []).map((item) => ({
          value: item.id,
          label: item.name,
          data: item
        }));
      } catch (error) {
        console.error('Error fetching entities:', error);
        return [];
      }
    },
    [selectedType, getAllBrands, getAllRetailers]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadEntityOptions = useCallback(
    debounce((inputValue, callback) => {
      fetchEntities(inputValue).then(callback);
    }, 500),
    [fetchEntities]
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        id: data.entity?.value,
        type: selectedType,
        personal_name: data.personalName,
        email: data.businessEmail,
        password: data.password,
        role: data.role || undefined,
        phone: data.phone || undefined,
        enable_crm: data.enableCRM || false,
        pitti_plan_id: data.selectedPlan
      };

      const result = await setupPittiBrandRetailerAccount(payload);

      if (result?.statusCode === 200) {
        reset();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error setting up account:', error);
    }
  };

  return (
    <Row className="justify-content-center mt-3" style={{ width: '100%' }}>
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="mb-4">
            <h4
              className="card-title mb-1"
              style={{ fontWeight: '600', fontSize: '20px' }}
            >
              Send Pitti Invitation
            </h4>
          </div>

          {organizationError && (
            <Alert
              color="danger"
              className="mt-2 mb-4 mb-0 d-flex align-items-start gap-2"
              style={{ fontSize: '13px', padding: '10px 12px' }}
            >
              <AlertCircle size={16} className="flex-shrink-0 mt-1" />
              <div>
                <strong>{organizationError.message}</strong>
                {organizationError.organization?.name && (
                  <div className="mt-1">
                    Organization: <strong>{organizationError.organization.name}</strong>
                  </div>
                )}
              </div>
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row className="g-3">
              {/* Type Selection */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Type <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        invalid={!!errors.type}
                        style={{ fontSize: '14px' }}
                      >
                        <option value="brand">Brand</option>
                        <option value="retailer">Retailer</option>
                      </Input>
                    )}
                  />
                  {errors.type && (
                    <FormFeedback>{errors.type.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Entity Selection (Brand/Retailer) */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Select {selectedType === 'brand' ? 'Brand' : 'Retailer'}{' '}
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="entity"
                    control={control}
                    render={({ field }) => (
                      <AsyncSelect
                        {...field}
                        onChange={(option) => handleEntityChange(option, field)}
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadEntityOptions}
                        placeholder={`Type to search ${selectedType}...`}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? 'No results found' : 'Type to search...'
                        }
                        isClearable
                        isLoading={checkingOrganization}
                        className={errors.entity || organizationError ? 'is-invalid' : ''}
                        styles={{
                          control: (base) => ({
                            ...base,
                            fontSize: '14px',
                            borderColor: errors.entity || organizationError ? '#dc3545' : base.borderColor,
                            '&:hover': {
                              borderColor: errors.entity || organizationError ? '#dc3545' : base.borderColor
                            }
                          }),
                          option: (base) => ({ ...base, fontSize: '14px' })
                        }}
                      />
                    )}
                  />
                  {errors.entity && (
                    <div
                      className="text-danger"
                      style={{ fontSize: '12px', marginTop: '4px' }}
                    >
                      {errors.entity.message}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Personal Name */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Personal Name <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="personalName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="e.g John Doe"
                        invalid={!!errors.personalName}
                        style={{ fontSize: '14px' }}
                      />
                    )}
                  />
                  {errors.personalName && (
                    <FormFeedback>{errors.personalName.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Business Email */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Business Email <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="businessEmail"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="e.g john@company.com"
                        invalid={!!errors.businessEmail}
                        style={{ fontSize: '14px' }}
                      />
                    )}
                  />
                  {errors.businessEmail && (
                    <FormFeedback>{errors.businessEmail.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Role (Optional) */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Role
                  </Label>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="e.g Market Lead (Optional)"
                        invalid={!!errors.role}
                        style={{ fontSize: '14px' }}
                      />
                    )}
                  />
                  {errors.role && (
                    <FormFeedback>{errors.role.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Phone (Optional) */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Phone Number
                  </Label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="tel"
                        placeholder="92 123 456 7890 (Optional)"
                        invalid={!!errors.phone}
                        style={{ fontSize: '14px' }}
                      />
                    )}
                  />
                  {errors.phone && (
                    <FormFeedback>{errors.phone.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Password */}
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{ fontWeight: '500', fontSize: '14px' }}
                  >
                    Password <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <div className="position-relative">
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Min 7 chars with special character"
                          invalid={!!errors.password}
                          style={{ fontSize: '14px', paddingRight: '40px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center position-absolute top-50 end-0 translate-middle-y me-2"
                          style={{
                            right: 0,
                            width: '28px',
                            height: '28px',
                            cursor: 'pointer',
                            color: '#6c757d'
                          }}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    )}
                  />
                  {errors.password && (
                    <FormFeedback>{errors.password.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>

              {/* Enable CRM Toggle */}
              <Col md={12}>
                <FormGroup className="mb-0">
                  <div
                    className="d-flex align-items-center gap-3"
                    style={{ marginTop: '8px' }}
                  >
                    <Controller
                      name="enableCRM"
                      control={control}
                      render={({ field }) => (
                        <div className="form-check form-switch">
                          <Input
                            {...field}
                            type="switch"
                            id="enableCRM"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            style={{ cursor: 'pointer', width: '40px', height: '20px' }}
                          />
                          <Label
                            for="enableCRM"
                            className="form-check-label"
                            style={{
                              fontWeight: '500',
                              fontSize: '14px',
                              cursor: 'pointer',
                              marginLeft: '8px'
                            }}
                          >
                            Enable CRM
                          </Label>
                        </div>
                      )}
                    />
                  </div>
                </FormGroup>
              </Col>

              {/* Plan Selection */}
              <Col md={12}>
                <FormGroup className="mb-0">
                  <Label
                    className="form-label"
                    style={{
                      fontWeight: '500',
                      fontSize: '14px',
                      marginBottom: '12px'
                    }}
                  >
                    Select Plan <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="selectedPlan"
                    control={control}
                    render={({ field }) => (
                      <Row className="g-3">
                        {plans.map((plan) => (
                          <Col key={plan.id} md={6} lg={3}>
                            <div
                              onClick={() => field.onChange(plan.id)}
                              style={{
                                border:
                                  watchedPlan === plan.id
                                    ? '2px solid #0d6efd'
                                    : '1px solid #dee2e6',
                                borderRadius: '8px',
                                padding: '16px',
                                cursor: 'pointer',
                                backgroundColor:
                                  watchedPlan === plan.id ? '#f0f7ff' : '#fff',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                minHeight: '220px'
                              }}
                            >
                              {watchedPlan === plan.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    backgroundColor: '#0d6efd',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Check size={14} color="#fff" />
                                </div>
                              )}
                              <h5
                                style={{
                                  fontWeight: '600',
                                  fontSize: '16px',
                                  marginBottom: '8px',
                                  color: watchedPlan === plan.id ? '#0d6efd' : '#333'
                                }}
                              >
                                {plan.name}
                              </h5>
                              <div
                                style={{
                                  fontSize: '24px',
                                  fontWeight: '700',
                                  color: watchedPlan === plan.id ? '#0d6efd' : '#333',
                                  marginBottom: '12px'
                                }}
                              >
                                {plan.currency}
                                {plan.amount}
                                <span
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: '400',
                                    color: '#666'
                                  }}
                                >
                                  /{plan.type}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: '13px',
                                  color: '#666',
                                  lineHeight: '1.6'
                                }}
                              >
                                <div>
                                  <strong>Seats:</strong> {plan.seats}
                                </div>
                                <div>
                                  <strong>Contact Credits:</strong> {plan.contact_credit}
                                </div>
                                <div>
                                  <strong>Search Credits:</strong> {plan.max_search_credits}
                                </div>
                                <div>
                                  <strong>Match Credits:</strong> {plan.max_match_credits}
                                </div>
                                <div>
                                  <strong>CRM:</strong>{' '}
                                  {plan.crm_allowed ? 'Included' : 'Not Included'}
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  />
                  {errors.selectedPlan && (
                    <div
                      className="text-danger"
                      style={{ fontSize: '12px', marginTop: '8px' }}
                    >
                      {errors.selectedPlan.message}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-4 pt-2 border-top">
              <Button
                color="primary"
                type="submit"
                disabled={isSubmitting || !!organizationError || checkingOrganization}
                size="lg"
                style={{ minWidth: '200px', fontWeight: '500' }}
              >
                {isSubmitting && <Spinner size="sm" className="me-2" />}
                {isSubmitting ? 'Sending Invite...' : 'Send Pitti Invite'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </Row>
  );
};

export default ManualInviteTab;
